import React from "react";
import { useSelector } from "react-redux";
import { createStyles, InputAdornment, makeStyles, Theme } from "@material-ui/core";
import Backdrop from "@material-ui/core/Backdrop";
import moment from "moment";
import numeral from "numeral";
import * as yup from "yup";
import { v4 as uuidv4 } from "uuid";
import { FormikValues, FormikWrapper, NumberTextField, SelectField, TextField } from "../../formikFormInputs";
import { useActions } from "../../../actions";
import * as Actions from "../../../actions/estimation";
import * as AccountActions from "../../../actions/account";
import * as CarrierActions from "../../../actions/carrier";
import * as EstimatorActions from "../../../actions/estimator";
import * as ClientAction from "../../../actions/client";
import {
    contributionOptions,
    createEstimateReports,
    incomeOptions,
    isFormValid,
    MONTHLY,
    ProposalCommonProps,
    ProposalEnum,
    rateClassMappingObject,
    UCORIConstantKeyProps,
} from "./helper";
import { ProposalForm, UploadedDocumentTable } from "./index";
import Socket from "../../../pages/client/Estimation/V1/Socket";
import TestingProcessDialog from "../Estimation/TestingProcessDialog";
import { getMinimumDesireLifeTimeBenefits, webHookURL } from "../../../pages/client/Estimation/V1/Steppers";
import { ContributionAmount as contributionPayload } from "../../../pages/client/Estimation/V1/ContributionAmount";
import { DeathBenefit as deathBenefitPayload } from "../../../pages/client/Estimation/V1/DeathBenefit";
import { setOptions } from "../../../pages/client/Estimation/V1/Components/StepThreeTwo";
import { Label } from "../../formInputs";
import { RootState } from "../../../reducers";
import {getCarrierCode, isDebug, joinAsFullName, noop, sleep ,unmaskCurrency} from "../../../utils/commonUtils";
import {
    ATLANTIC_HEALTH,
    CallingFromObject,
    cashStreamSlug,
    IUL710PayEnum, IUL710TermEnum,
    NationalLifeCarrierDetail, primaryPlansOptions,
    GWT_GEN2_STRATEGY, REQUIRED_FIELD
} from "../../../constants";
import { ANNUALLY } from "../../../pages/client/Estimation/V1/Components/ContributionFrequency";
import { useCreateAllocationPayLoad } from "../../../pages/client/Estimation/Hooks/Hooks";
import {
    addPlus,
    calculateGrowthRate,
    carrierRiders, createAllocationObject,
    createLivingBenefits, createLivingBenefitsFromRiders,
    distributionLoanRate,
    getAllocationPayloadForAdmin,
    InvestmentValues,
    isALGCarrier,
    stressAllocationForALZ,
    stressAllocationsData,
} from "../../../pages/client/Estimation/utils/utils";
import { submitIULEstimation } from "../../../pages/client/Estimation/V1/Components/EstimationFormikHandler";
import { isNfIulCase } from "../../../utils/commonFunc";
import LabelWithIcon from "./LabelWithIcon";
import GenericDialog, { GenericDialogProps } from "../../dialog/GenericDialog";
import textField from "../../formikFormInputs/TextField";
import Button from "@material-ui/core/Button";
import EditIcon from "@material-ui/icons/Edit";
import { required } from "../../../utils/validation";


type UHS = {
    callingFrom: string,
    clientId: number,
    functionCallOnUUid: Function,
    functionOnDataLoadingEnable: Function,
    issueAge: number | null,
    clientPolicyId: string
}

const frequencyMappingObject = {
    'annual': ANNUALLY,
    'monthly': MONTHLY,
}

interface ContentProps extends ProposalCommonProps {
    fileName: string;
    setFileName: Function;
    setHasReports: Function;
    setLoading?: Function;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: "100%",
        },
        button: {
            marginRight: theme.spacing(1),
        },
        instructions: {
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(1),
        },
        backdrop: {
            zIndex: theme.zIndex.drawer + 1,
            color: "#000",
        },
    }),
);

const getContributionAmount = (contributionAmmount: number, paymentFrequency: (typeof ANNUALLY | typeof MONTHLY)): number => {
    if (paymentFrequency === ANNUALLY) {
        return Number(unmaskCurrency(contributionAmmount && contributionAmmount.toString()));
    } else {
        return Number(unmaskCurrency(contributionAmmount && contributionAmmount.toString())) * 12;
    }
};

const useFields = ({ clientPolicy, issueAge, isChanged, setIssueAge, setIsChanged,setEditIssueAge }) => {
    const carrierId: number = clientPolicy && clientPolicy.carrier && clientPolicy.strategy ? clientPolicy.carrier.id : NationalLifeCarrierDetail.value;
    const ageIncrement: number = NationalLifeCarrierDetail.id === carrierId ? 15 : 16; // 15 in case of NL and 16 in case of AL - NIWC-2721
    const carrierActions = useActions(CarrierActions);
    const userDetail = useSelector((root: RootState) => root.clientAccount);
    const minCon = useSelector((state: RootState) => state.minimumContribution) || 21350;
    const {getLivingBenefitsList} = useActions(AccountActions);
    let atlantic = (ATLANTIC_HEALTH.includes(clientPolicy && clientPolicy.company && clientPolicy.company.companyLinkUrl as any) && userDetail.role === 6)
    const [livingBenefits, setLivingBenefits] = React.useState<object[]>([]);
    const [actualHealthRatingOption, setActualHealthRatingOption] = React.useState([]);
    const [beginDistributions, setBeginDistributions] = React.useState(setOptions(65));
    const [endDistributions, setEndDistributions] = React.useState<any[]>(setOptions(65));
    const isIulCase = isNfIulCase((clientPolicy && clientPolicy.strategy && clientPolicy.strategy.id) as number, (clientPolicy && clientPolicy.strategy && clientPolicy.strategy.slug) as string);

    React.useEffect(() => {
        livingBenefitsDropDown().then();
    }, [carrierId]);
    React.useEffect(() => {
        console.info({ issueAge, ageIncrement });
        if (!!issueAge) {
            const beginDistVal: number = parseInt(issueAge) + ageIncrement;
            const res: any[] = setOptions(beginDistVal);
            setBeginDistributions(res);
            setEndDistributions(res);
        }
    }, [issueAge]);
    React.useEffect(() => {
        if (clientPolicy && clientPolicy.carrierId) {
            carrierActions.getCarrierActualHealthTitle(clientPolicy.carrierId).then((res) => {
                const actualHealthArr = res && res.length && res.map((item) => {
                    return {
                        key: item.title,
                        value: item.title,
                        name: item.title,
                    };
                }) || [];
                setActualHealthRatingOption(actualHealthArr);
            });
        }
    }, []);

    const livingBenefitsDropDown = async () => {
        let livingBenefitsArr: object[] = [];

        const result = await getLivingBenefitsList();
        if (result && result.length > 0) {
            result.forEach((item: any) => {

                const pushTo: object = {
                    key: item.id,
                    value: item.id,
                    name: item.name,
                };
                if (carrierRiders[clientPolicy.carrierId].includes(item.id)) {
                    livingBenefitsArr.push(pushTo);
                }
            });
        }
        setLivingBenefits(livingBenefitsArr);
    };

    const UploadProposalFields = () => {
        const fields: any = [
            {
                className: "mt3",
                component: SelectField,
                id: "livingBenefit",
                isRequired: true,
                label: <Label label={"Living Benefits"} />,
                name: "livingBenefit",
                onSelectChange: noop,
                options: livingBenefits,
                multiple: true,
                required: true,
                // value:[],
                size: 6,
                variant: "outlined",
            },
            {
                className: "mt3",
                component: SelectField,
                id: "healthRating",
                isRequired: true,
                label: <Label label={"Actual Health Rating"} required/>,
                name: "healthRating",
                onSelectChange: noop,
                options: actualHealthRatingOption,
                required: true,
                size: 6,
                variant: "outlined",
            },
            {
                className: "cursorPointer",
                id: "issueAge",
                name: "issueAge",
                label:  <LabelWithIcon label="Issue Age" isChanged={isChanged} required={true} />,
                isRequired: true,
                component: NumberTextField,
                variant: "outlined",
                defaultValue: issueAge,
                size: 6,
                addToOnChange: (e: any) => {
                    if (issueAge && e.target.value == issueAge) {
                        return;
                    }
                    setIsChanged(true)
                    setIssueAge(e.target.value);
                },
                validate: (value)=> {
                    if (value === null || value === undefined || value === "") {
                        return "Age must be between 18 and 65";
                    }
                    const num = typeof value === 'number'
                            ? value : Number(String(value).trim());
                    if (Number.isNaN(num)) {
                        return "Age must be between 18 and 65";
                    }
                    if (num < 18 || num > 65) {
                        return "Age must be between 18 and 65";
                    }
                    return;
                },
                onClick: () => !isChanged && setEditIssueAge(true),
                InputLabelProps: {shrink: !!issueAge},
            }
        ];


        if(!isIulCase){
            fields.unshift({
                className: "mt3",
                component: NumberTextField,
                id: "tranche",
                label: <Label label="Tranche No" required/>,
                name: "tranche",
                onChange: noop,
                size: 6,
            },)
        }
        return fields;
    }





    const contributionTypeConditionalFields = (values: any) => {


        const fields: any = [];
        if (values.contributionType == contributionOptions.contribution.value) {
            fields.push({
                className: "mt3",
                component: NumberTextField,
                id: "deathBenefitAmount",
                label: <Label label="Death Benefit Amount" required/>,
                name: "deathBenefitAmount",
                onChange: noop,
                autoComplete:"off",
                size: 6,
            })
        }
        if (values.contributionType == contributionOptions.deathBenefits.value ) {
            fields.push({
                className: "mt3",
                component: NumberTextField,
                id: "contributionAmount",
                label: <Label label="Contribution Amount" required/>,
                name: "contributionAmount",
                onChange: noop,
                autoComplete:"off",
                size: 6,
                // helperText: atlantic ? `` : `Minimum ${maskCurrency(minCon + "")} contribution required`
            })
        }
        if(isIulCase){
            fields.push( {
                className: "mt20",
                component: SelectField,
                size: 5,
                label: <Label label={"Contribution Years"} required={true}/>,
                name: "primaryFocusPlan",
                options: primaryPlansOptions,
            })
        }
        return fields;
    }

    const incomeConditionalFields = (values: any) => {
        const fields: any = [];
        if (values.incomeOptions == incomeOptions.income.value) {
            fields.push({
                id: "incomeStartAge",
                component: SelectField,
                label: <Label label="Income Start Age" required/>,
                name: "incomeStartAge",
                size: 6,
                className: "mt3",
                options: beginDistributions,

            });
            fields.push({
                id: "incomeEndAge",
                component: SelectField,
                label: <Label label="Income End Age" required/>,
                name: "incomeEndAge",
                size: 6,
                className: "mt3",
                required: true,
                options: endDistributions,
            })
        }
        return fields;
    }

    return {UploadProposalFields, contributionTypeConditionalFields, incomeConditionalFields,};
};

const useHandleSubmit = ({clientId, functionCallOnUUid, functionOnDataLoadingEnable, callingFrom, issueAge, clientPolicyId}: UHS) => {
    const estimationActions = useActions(Actions);
    const estimatorActions = useActions(EstimatorActions);
    const {getClient} = useActions(ClientAction);
    const loggedInUser = useSelector((root: RootState) => root.auth.user);
    const userDetail = useSelector((root: RootState) => root.clientAccount);
    const createAllocationPayLoad = useCreateAllocationPayLoad("", "", clientId)

    const firstRun = () => {
        (
            async () => {
                await getClient(clientId, clientPolicyId);
            }
        )();
    };

    React.useEffect(firstRun, []);

    const runEstimatorForIul = async ({values}) =>{
        const {client, role} = userDetail;
        const clientPolicy:any =  client && client.clientPolicies && client.clientPolicies.length > 0 && client.clientPolicies[0];
        const { carrierId = 1, strategyId = 1 } = clientPolicy;
        const estimatorData = await estimatorActions.getEstimatorDetails({carrierId, strategyId});
        // let primaryFocusPlan = IUL710PayEnum.IUL7Pay;
        // let estimationDetails: any = await estimationActions.getEstimationDetailsByUserId(userDetail.id, callingFrom, clientPolicyId);
        // const responsePayload: any = JSON.parse(estimationDetails && estimationDetails.stressResponse);
        // if(responsePayload.success && responsePayload.success.common && responsePayload.success.common.premiumterm){
        //     primaryFocusPlan = responsePayload.success.common.premiumterm
        // }
        let allocationValues = {};
        if (estimatorData.id) {
            const res = await estimationActions.getAllocationQuestions({
                estimatorId: estimatorData.id,
                carrierId: carrierId,
            });
            if (res) {
                res.forEach((fields) => {
                    allocationValues[fields.code] = fields;
                });
            }
        }
        const rateClass =  rateClassMappingObject[values.healthRating as string] ? rateClassMappingObject[values.healthRating as string] : rateClassMappingObject['default']; // Possible Values 'Elite_NT' | 'Preferred_NT' | 'Select_NT' | 'Standard_NT' | 'Express_Standard_NT1' | 'Express_Standard_NT2' | 'Preferred_Tobacco' | 'Standard_Tobacco' | 'Express_Standard_Tobacco';

        const payload = {
            agentName: joinAsFullName(clientPolicy && clientPolicy.agent && clientPolicy.agent.user as any),
            atlantic: false,
            callingFrom,
            clientPolicy,
            estimationActions,
            estimatorData,
            issueAge: issueAge,
            setIsEstimationDataLoading: functionOnDataLoadingEnable,
            setRequestId: functionCallOnUUid,
            user: userDetail,
            userId: userDetail.id,
            role: role,
            userName: joinAsFullName(userDetail),
            allocationData: getAllocationPayloadForAdmin(values, carrierId, allocationValues),
            values: {
                employerContribution: values.employerContribution,
                contributionFrequency: values.contributionFrequency,
                annualContribution: values.contributionAmount,
                endDistributions: numeral(values && values.incomeEndAge).value(),
                beginDistributions: numeral(values && values.incomeStartAge).value(),
                tobacco: (rateClass.split("_") && rateClass.split("_")[1] == "Tobacco") ? "used" : "notUsed"  ,
                healthType: rateClass.split("_") ? rateClass.split("_")[0] : "Standard", // Standard or ?
                primaryFocusPlan: values.primaryFocusPlan, // need to update it dynamicly
                carrierId,
                dob: client && client.dob,
                InvestmentType: client && client.clientInvestmentType,
            }
        };

        if (values.isBlendEnabled && values.blendPercentage) {
            Object.assign(payload.values, {
                blendPercentage: [
                    {
                        agefrom: issueAge,
                        ageto:  issueAge,
                        blend: values && values.blendPercentage / 100,
                    }
                ]
            });
        }
        submitIULEstimation(payload).then()
    }


    const handleRunIllustration = async (props: FormikValues) => {
        if (!userDetail) {
            return;
        }

        const {client, role} = userDetail;
        const clientPolicy:any =  client && client.clientPolicies && client.clientPolicies.length > 0 && client.clientPolicies[0];

        if (!client) {
            return;
        }

        const { userId } = client;
        const { carrierId = 1, strategyId = 1 } = clientPolicy;
        const clientFullName: string = joinAsFullName(userDetail);
        let agentFullName: string = '';
        if (clientPolicy && clientPolicy.agent && clientPolicy.agent.user) {
            agentFullName = joinAsFullName(clientPolicy.agent.user);
        }

        const carrierCode = getCarrierCode(carrierId);
        const uuid = uuidv4();
        const webHookUrl: string = `${webHookURL}/${userId}/${loggedInUser.id}?callingFrom=${callingFrom}`;

        const {values}: any = props;

        /*
        when we have death benefit, send 0 for contributionamount, and run.solvefor = Contribution_Amount
        When we have contribution amount, send 1000000 for totaldeathbenefit and run.solvefor = Death_Benefit
        */

        functionOnDataLoadingEnable(true);
        functionCallOnUUid(uuid);

        const estimatorData = await estimatorActions.getEstimatorDetails({carrierId, strategyId});
        let allocationValues = {};
        if (estimatorData.id) {
            const res = await estimationActions.getAllocationQuestions({
                estimatorId: estimatorData.id,
                carrierId: carrierId,
            });
            if (res) {
                res.forEach((fields) => {
                    allocationValues[fields.code] = fields;
                });
            }
        }

        const rateClass: string = values.healthRating as string;

        let payload: any = {};

        if (values && values.incomeOptions === incomeOptions.deathBenefitsOnly.value) {
            if (values && values.contributionType === contributionOptions.contribution.value) {
                payload = {...contributionPayload};
                payload["common"]["contributionamount"] = 0; // todo userdetails
                payload["common"]["totaldeathbenefit"] = numeral(values && values.deathBenefitAmount).value();
            } else if (values && values.contributionType === contributionOptions.deathBenefits.value) {
                payload = {...deathBenefitPayload};
                payload['run']['deathbenefitonly'] = true;
                payload["common"]["totaldeathbenefit"] = 1000000;
                payload["illustration"]["deathbenefitchangeyear"] = "A101";
                payload["comparison"]["deathbenefitchangeyear"] = "A101";
                if (carrierCode === "ALZ") {
                    payload["illustration"]["deathbenefitchangeyear"] = "A100";
                    payload["comparison"]["deathbenefitchangeyear"] = "A100";
                }
                if(!!values.totalCombinedContribution){
                    payload["common"]["contributionamount"]  = values.totalCombinedContribution
                }else{
                    payload["common"]["contributionamount"] = getContributionAmount(numeral(values && values.contributionAmount).value(), values.contributionFrequency) ;
                }
            }
        }

        if (values && values.incomeOptions === incomeOptions.income.value) {
            payload = {...deathBenefitPayload};
            payload['run']['deathbenefitonly'] = false;
            payload["common"]["incomeagefrom"] = numeral(values && values.incomeStartAge).value();
            payload["common"]["incomeageto"] = numeral(values && values.incomeEndAge).value();
            payload["common"]["totaldeathbenefit"] = 1000000;
            if(!!values.totalCombinedContribution){
                payload["common"]["contributionamount"]  = values.totalCombinedContribution
            }else {
                payload["common"]["contributionamount"] = getContributionAmount(numeral(values && values.contributionAmount).value(), values.contributionFrequency);
            }
            payload["common"]["contributionamountemployer"] = !!values.employerContribution ? numeral(values.employerContribution).value() : 0;
        }

        payload["id"] = uuid;
        payload["webhook"] = webHookUrl;
        if(clientPolicyId){
            payload["webhook"] = webHookUrl + `&clientPolicyId=${clientPolicyId}`;
        }
        payload["InvestmentType"] = addPlus(values["plus"], values["InvestmentType"]);
        payload["common"]["fullname"] = clientFullName;
        payload["common"]["gender"] = (client && client.gender === 1) ? "Male" : "Female";
        payload["common"]["agent"]["fullname"] = agentFullName;
        payload["common"]["issueage"] = issueAge || (clientPolicy && clientPolicy.issueAge);
        payload["common"]["rateclass"] = rateClassMappingObject[rateClass] ? rateClassMappingObject[rateClass] : rateClassMappingObject['default']; // Possible Values 'Elite_NT' | 'Preferred_NT' | 'Select_NT' | 'Standard_NT' | 'Express_Standard_NT1' | 'Express_Standard_NT2' | 'Preferred_Tobacco' | 'Standard_Tobacco' | 'Express_Standard_Tobacco';
        payload["common"]["dateofbirth"] = moment(userDetail && userDetail.client && userDetail.client.dob).format("MM/DD/YYYY");
        /* Update estimator data on payload. Note: Estimator Data is provided by db */
        payload["common"]["riders"] = createLivingBenefits(values.livingBenefit);
        if (estimatorData) {
            let commonGrowthRate =  payload["common"]["growthrate"];
            payload["stress"]["loanassumptions"]["assumptions"] = estimatorData["stressLoanRates"].split(",") || payload["stress"]["loanassumptions"]["assumptions"];
            payload["illustration"]["loanassumptions"]["assumptions"] = estimatorData["illustrationLoanRates"].split(",") || payload["illustration"]["loanassumptions"]["assumptions"];
            payload["common"]["growthrate"] = commonGrowthRate;
            payload["common"]["capitalgainstaxrate"] = estimatorData["capitalGainRate"] || payload["common"]["capitalgainstaxrate"];
            payload["common"]["managementfee"] = estimatorData["managementFee"] || payload["common"]["managementfee"];
            payload["common"]["statetaxrate"] = estimatorData["averageStateIncomeTaxRate"] || payload["common"]["statetaxrate"];
            payload["common"]["distributionloanrate"] = estimatorData["distributionLoanRate"] || payload["common"]["distributionloanrate"];
            payload["common"]["afrrate"] = estimatorData["afrRate"] || payload["common"]["afrrate"];
            payload["common"]["arrangementfee"] = estimatorData["arrangementFee"] || payload["common"]["arrangementfee"];
            payload["stress"]["loanassumptions"]["discountcashvaluelowpoint"] = estimatorData["stressTestCashValueLowPoint"] || payload["stress"]["loanassumptions"]["discountcashvaluelowpoint"];
            payload["illustration"]["loanassumptions"]["discountcashvaluelowpoint"] = estimatorData["illustrationTestCashValueLowPoint"] || payload["illustration"]["loanassumptions"]["discountcashvaluelowpoint"];
            payload["stress"]["loanassumptions"]["bankdiscountfirstyear"] = estimatorData["stressBankDiscountFirstYear"] || payload["stress"]["loanassumptions"]["bankdiscountfirstyear"];
            if(createAllocationPayLoad){
                // const allocationData = await createAllocationPayLoad(addPlus(values["plus"], values["InvestmentType"]), {InitEstimatorId:estimatorData.id , runTimeCarrierId:carrierId});
                const allocationData = getAllocationPayloadForAdmin(values, carrierId, allocationValues);

                const allocations = allocationData[0]["allocations"]
                const growthRate =  calculateGrowthRate(allocations);
                if(growthRate){
                    payload["common"]["growthrate"] = growthRate/100;
                    payload["common"]["distributionloanrate"] = distributionLoanRate(growthRate, carrierId, estimatorData['distributionLoanRate']);
                }
                payload["illustration"]["interestrates"] =  allocationData;
                payload["comparison"]["interestrates"] =  allocationData;
                payload["stress"]["interestrates"] = isALGCarrier(carrierId) ? stressAllocationForALZ : stressAllocationsData;
            }
            // payload["stress"]["interestrates"][1].creditsuisserate = growthRate;
            // payload["stress"]["interestrates"][1].uspacesetterrate = growthRate;
            // payload["stress"]["interestrates"][3].creditsuisserate = growthRate;
            // payload["stress"]["interestrates"][3].uspacesetterrate = growthRate;
            // payload["stress"]["interestrates"][5].creditsuisserate = growthRate;
            // payload["stress"]["interestrates"][5].uspacesetterrate = growthRate;
            payload["illustration"]["loanassumptions"]["bankdiscountfirstyear"] = estimatorData["illustrationBankDiscountFirstYear"] || payload["illustration"]["loanassumptions"]["bankdiscountfirstyear"];

            // Preferring admin-user-entered blend percentage from form (when provided), otherwise fallback to estimator data/previous value
            payload["common"]["blendpercentage"] = (values.isBlendEnabled && values.blendPercentage) ?
                    [
                        {
                            agefrom: payload["common"]["issueage"],
                            ageto: payload["common"]["issueage"],
                            blend: values && values.blendPercentage / 100,
                        },
                    ] :
                    estimatorData["estimatorDataBlend"] || payload["common"]["blendpercentage"];
        }
        payload["common"]["monthlypayclientextra"] = 0.05;
        payload["common"]["paymentfrequency"] = values.contributionFrequency;
        payload["common"]["paymentfrequencyemployer"] = "Annual";
        payload["common"]["carrier"] = carrierCode;
        console.log("== payload[common]", payload["common"]);
       await estimationActions.getEstimatesFromCloud(payload, role, {...values, issueAge: issueAge || clientPolicy && clientPolicy.issueAge, agentName: agentFullName, userName: clientFullName}, userId, callingFrom, clientPolicy.id);
    };

    return [handleRunIllustration, runEstimatorForIul];
};

const Content: React.FC<ContentProps> = (props) => {
    const DEFAULT_MIN_CON: number = 21350;
    const classes = useStyles();
    const callingFrom = CallingFromObject.CLIENT_ILLUSTRATION;
    const initUPFormInit = {
        contributionAmount: "",
        contributionType: '',
        deathBenefitAmount: "",
        healthRating: "",
        incomeEndAge: "",
        incomeOptions: "",
        incomeStartAge: "",
        livingBenefit: [],
        tranche: "",
        contributionFrequency: ANNUALLY,
        contributionTypeToggle:"0",
        InvestmentType: "",
        issueAge: "",
        // custom term blend controls
        isBlendEnabled: false,
        blendPercentage: "",
    };
    const {
        client,
        clientId,
        enrollmentId,
        fileName,
        proposalUpdate,
        setFileName,
        setHasReports,
        refreshList,
        setUploadProposalPopUp,
        actionId,
        clientPolicy,
        setLoading
    } = props;
    const clientPolicyId = clientPolicy && clientPolicy.id;
    const isIulCase = isNfIulCase((clientPolicy && clientPolicy.strategy && clientPolicy.strategy.id) as number, (clientPolicy && clientPolicy.strategy && clientPolicy.strategy.slug) as string);
    const inputRefs = React.useRef({});
    const validateObject: any = {
        tranche:  isIulCase ? undefined : yup.string().required(REQUIRED_FIELD).nullable(),
        contributionType: yup.string().required(REQUIRED_FIELD),
        healthRating: yup.string().required(REQUIRED_FIELD),
        incomeOptions: yup.string().required(REQUIRED_FIELD),
        deathBenefitAmount: yup.string(),
        contributionFrequency: yup.string().required(REQUIRED_FIELD),
        primaryFocusPlan: isIulCase ? yup.string().required("Contribution Years required") : undefined,
        issueAge: yup.number().typeError("Issue Age must be a number").required(REQUIRED_FIELD).nullable(),
        isBlendEnabled: yup.boolean(),
        blendPercentage: yup.number().typeError("Custom Term Blend must be a number").min(0, 'Minimum value is 0').max(100, 'Maximum value is 100').when('isBlendEnabled', {
            is: true,
            then: (s) => s.required('Blend Percentage Required'),
            otherwise: (s) => s.notRequired(),
        }),
    };
    const uploadFormSchema = yup.object({
        ...validateObject,
        contributionAmount: yup.string(),
    });
    const runIllustrationFormSchema: any = yup.object(validateObject);

    const estimationActions = useActions(Actions);
    const accountActions = useActions(AccountActions);
    const loggedInUser = useSelector((store: RootState) => store.auth.user);
    const minCon = useSelector((state: RootState) => state.minimumContribution) || DEFAULT_MIN_CON;
    const userDetail = useSelector((root: RootState) => root.clientAccount);
    const [uploadCustomOrRunIllustration, setUploadCustomOrRunIllustration] = React.useState<UCORIConstantKeyProps>(ProposalEnum.RUN_ILLUSTRATION);
    const [isIllustrationFilesCreated, setIllustrationFilesCreated] = React.useState(false);
    const [illustrationFiles, setIllustrationFiles] = React.useState();
    const [csvFileName, setCsvFileName] = React.useState(""); //csv filename
    const [illustrationFileName, setIllustrationFileName] = React.useState(""); // client illustration
    const [issueAge, setIssueAge] = React.useState<number | null>(null);
    const [cashStream, setCashStream] = React.useState(false); // cash stream strategy
    const [employerOnly, setEmployerOnly] = React.useState(false); // employer only
    const [issuedCon, setIssueCon] = React.useState(true);
    const [isEstimationDataLoading, setIsEstimationDataLoading] = React.useState<boolean>(false);
    const [isReportRequestCompleted, setIsReportRequestCompleted] = React.useState<boolean>(false);
    const [requestId, setRequestId] = React.useState<null | string>(null);
    const [caseDetails, setCaseDetails] = React.useState<any>(null);
    const [isReportRequestAutomated, setIsReportRequestAutomated] = React.useState<boolean>(false);
    const [isReportDataLoading, setIsReportDataLoading] = React.useState<boolean>(false);
    const [upFormData, setUPFormData] = React.useState<any>(initUPFormInit);
    const [stressRequest, setStressRequest] = React.useState<any>(null);
    const [isChanged, setIsChanged] = React.useState(false);
    const [isEditIssueAge, setEditIssueAge] = React.useState(false);
    const [activeField, setActiveField] = React.useState("");
    const formRef = React.useRef<any>();
    let atlantic = (ATLANTIC_HEALTH.includes(clientPolicy && clientPolicy.company && clientPolicy.company.companyLinkUrl as any) && userDetail.role === 6)
    const isGWTGen2 = clientPolicy && clientPolicy.strategy && clientPolicy.strategy.id === GWT_GEN2_STRATEGY;
    if (!loggedInUser) {
        console.info(isIllustrationFilesCreated, issuedCon, isReportRequestCompleted);
    }


    const extractCF = (caseDetails) =>{
        return (caseDetails && frequencyMappingObject[caseDetails.paymentFrequency]) ? frequencyMappingObject[caseDetails.paymentFrequency]  : ANNUALLY
    }
    const jVals = [
        {
            exactFileName: "Client Proposal - " + moment().format("DD-MM-YYYY HH:mm:ss"),
            fileName,
            name: "Upload Proposal",
            setFileName,
            showOnProposalUpdate: true, // TODO proposalUpdate,
            valName: "proposalFile",
        },
        {
            exactFileName: "Client Illustration - " + moment().format("DD-MM-YYYY HH:mm:ss"),
            fileName: illustrationFileName,
            name: "Upload Illustration",
            setFileName: setIllustrationFileName,
            showOnProposalUpdate: true, // TODO !proposalUpdate,
            valName: "illustrationFile",
        },
        {
            exactFileName: "Client Illustration CSV - " + moment().format("DD-MM-YYYY HH:mm:ss"),
            fileName: csvFileName,
            name: "Upload CSV",
            setFileName: setCsvFileName,
            showOnProposalUpdate: true, // TODO !proposalUpdate,
            valName: "csvFile",
        },
    ];

    const {
        UploadProposalFields,
        contributionTypeConditionalFields,
        incomeConditionalFields
    } = useFields({ clientPolicy, issueAge, isChanged, setIssueAge, setIsChanged,setEditIssueAge }); // isEditIssueAge

    const [handleRunIllustration, runEstimatorForIul] = useHandleSubmit({
        clientId,
        functionCallOnUUid: (uuid: string) => setRequestId(uuid),
        functionOnDataLoadingEnable: () => setIsEstimationDataLoading(true),
        callingFrom,
        issueAge,
        clientPolicyId
    });

    // upload proposal/illustration
    const handleUploadProposal = async (values: any) => {
            let issuedContribution = values.totalCombinedContribution ? values.totalCombinedContribution : getContributionAmount(values.contributionAmount,values.contributionFrequency); // contributionFrequency
            let employerContribution = Number(unmaskCurrency(values.employerContribution) || "");
            let deathBenefit = Number(unmaskCurrency(values.deathBenefitAmount.toString()));
            if (cashStream || employerOnly) {
                issuedContribution = 0;
            }
            setLoading &&setLoading(true);
            if (proposalUpdate) {
                await accountActions.replaceProposalForAdvisor({
                    issuedContribution: issuedContribution,
                    employerContribution: employerContribution,
                    deathBenefit: deathBenefit,
                    fileName,
                    clientPolicyId: clientPolicy.id,
                    clientId
                });
            } else {
                await accountActions.uploadProposalForAdvisor({
                    issuedContribution: issuedContribution,
                    employerContribution: employerContribution,
                    deathBenefit: deathBenefit,
                    "fileName": {"uploadProposal": fileName, "uploadIllustration": illustrationFileName, "uploadCsv": csvFileName},
                    clientPolicyId: clientPolicy.id,
                    agentId: caseDetails.agentId,
                    enrollmentId,
                    uploadType: uploadCustomOrRunIllustration === ProposalEnum.UPLOAD_CUSTOM_ILLUSTRATION && !fileName && !illustrationFileName && !csvFileName ? ProposalEnum.RUN_ILLUSTRATION : uploadCustomOrRunIllustration,
                    actualHealthRating: values.healthRating,
                    livingBenefits: values.livingBenefit,
                    frequency: values.contributionFrequency,
                    actionItemId: actionId,
                    allocations: createAllocationObject(values),
                    issueAge: values.issueAge,
                });
                // uploadType = RUN_ILLUSTRATION if no file is uploaded and UPLOAD_CUSTOM_ILLUSTRATION if file is uploaded
                // we are assuming RUN_ILLUSTRATION default to map case detail for all clients
                // if admin uploads custom files no need to run mapping,
                // but if they chose to upload but didn't upload any files, we assume no upload, use existing proposal
            }
            setLoading && setLoading(false);
            setUploadProposalPopUp(false);
            if (typeof refreshList === "function") {
                refreshList();
            }
    };

    const validate = (values: FormikValues) => {
        const healthRating = rateClassMappingObject[values.healthRating] ? rateClassMappingObject[values.healthRating] : rateClassMappingObject['default'];
        let minDeathBenefitAmount = 100000;
        if (caseDetails) {
            const minConPayload = {
                gender: caseDetails.gender,
                issueAge: Number(caseDetails.issueAge)
            };
            minDeathBenefitAmount = getMinimumDesireLifeTimeBenefits(minConPayload, healthRating);
        }
        // we are sending atlantic we need same functionality for all user
        const errors = isFormValid(values,{minContribution:minCon , minDeathBenefitAmount, atlantic:true  });
        return errors.fieldsErrors
    };
    const getClientCaseDetails = async (clientPolicyId: number) => {
        const details = await accountActions.getCaseDetails(clientPolicyId);
        setCaseDetails(details);
    };
    const socketIsReportDataLoading = (value: any) => {
        setIsReportDataLoading(value);
        if (value === false) {
            setIllustrationFilesCreated(true);
            setUploadCustomOrRunIllustration(ProposalEnum.RUN_ILLUSTRATION as UCORIConstantKeyProps);
        }
    };
    const socketButtonClick = (loader: any) => {
        const companyLinkUrl = caseDetails && caseDetails.company && caseDetails.company.companyLinkUrl ;
        createEstimateReports({
            initRequest: () => {
                if (loader) {
                    setIsReportRequestAutomated(false);
                } else {
                    setIsReportRequestAutomated(true);
                }
            },
            onReportsAlreadyExist: (reports) => {
                setIsReportDataLoading(false);
                setIsReportRequestCompleted(true);
            },
            onRequestIdCreated: (id) => {
                setRequestId(id)
            },
            callingFrom,
            estimationActions,
            user: {
                clientPolicy: caseDetails,
                loggerInUserId: loggedInUser.id,
                loggerInUserRole: loggedInUser.role,
                id: caseDetails && caseDetails.client.userId,
                role: caseDetails && caseDetails.client.user.role,
                companyLinkUrl
            }
        }).then();
    }
    const calculateIssueAge = async (dob: string, carrierId: number) => {
        const response: any = await estimationActions.getIssueAge(dob, carrierId);
        setIssueAge(response && response.issueAge);
    };

    const fetchCaseDetail = React.useCallback(() => {
        if (clientPolicy) {
            getClientCaseDetails(clientPolicy.id).then(() => {
            });
        }
    }, []);

    const firstTimerCall = async () => {
        fetchCaseDetail()
    };

    const handleRemoveFocus = () => {
        const el = inputRefs.current[activeField];
        if (el && typeof el.blur === "function") el.blur();
    };
    React.useEffect(() => {
        firstTimerCall().then();
    }, []);

    React.useEffect(() => {
        if (clientPolicy && clientPolicy.strategy && clientPolicy.strategy.slug === cashStreamSlug) {
            setCashStream(true);
        }
        if (clientPolicy && clientPolicy.company && clientPolicy.company.contributionType &&
            clientPolicy.company.contributionType === "EMPLOYER_ONLY") {
            setEmployerOnly(true);
        }
    }, [client]);

    React.useEffect(() => {
        if (cashStream || employerOnly) {
            setIssueCon(false);
        }
    }, [cashStream, employerOnly]);
    //
    React.useEffect(() =>{
        (async ()=>{
          if(client && client.userId && clientPolicyId){
              let estimationDetails: any = await estimationActions.getEstimationDetailsByUserId(client && client.userId, callingFrom, clientPolicy && clientPolicy.id);
              if (!!(estimationDetails && estimationDetails.stressResponse)) {
                  let request: any = JSON.parse(estimationDetails && estimationDetails.stressRequest);
                  // need to set initial Values
                  setStressRequest(request);
                  // console.log(createLivingBenefitsFromRiders(responsePayload.success.common.riders), "=== dddd")

              }
          }
        })()
    },[client && client.userId, clientPolicyId]);

    React.useEffect(() => {
        if (caseDetails) {
            calculateIssueAge(caseDetails && caseDetails.client && caseDetails.client.dob, clientPolicy && clientPolicy.carrierId).then();
        }}, [caseDetails]);

    React.useEffect(() => {
        if (caseDetails) {
            // calculateIssueAge(caseDetails && caseDetails.client && caseDetails.client.dob, clientPolicy && clientPolicy.carrierId).then();
            const isAlZ =  getCarrierCode(caseDetails.carrierId) == "ALZ"
            let {trancheNo, contributionAmount, deathBenefit, actualHealthRating, livingBenefits} = caseDetails;
            let livingBenefit = livingBenefits ? livingBenefits.map((s: any) => s.livingBenefitId) : [];
            let contributionType = atlantic ? contributionOptions.deathBenefits.value : "";
            const formValues:any = (formRef && formRef.current && formRef.current.values) || {}
           // if estimation is already run
            if ( stressRequest ) {
                if (stressRequest.common){
                    if(stressRequest.common.contributionAmount){
                        contributionAmount = stressRequest.common.contributionAmount;
                    }
                    if (stressRequest.common.incomeagefrom && stressRequest.common.incomeageto) {
                        upFormData.incomeStartAge = stressRequest.common.incomeagefrom;
                        upFormData.incomeEndAge = stressRequest.common.incomeageto;
                    }
                    if (stressRequest.common.contributionamountemployer){
                        upFormData.employerContribution = stressRequest.common.contributionamountemployer
                    }
                    if (stressRequest.common.riders && Object.keys(stressRequest.common.riders).length > 0) {
                        livingBenefit = createLivingBenefitsFromRiders(stressRequest.common.riders)
                    }
                    if(stressRequest.common.rateclass){
                        Object.keys(rateClassMappingObject).findIndex((key, index)=>{
                            if(stressRequest.common.rateclass == rateClassMappingObject[key]){
                                actualHealthRating = key
                                return true;
                            }
                        })
                    }
                }
                if (stressRequest['run']['deathbenefitonly'] === false) {
                    upFormData.incomeOptions = incomeOptions.income.value;
                    // Handle the case where death benefit only is false
                }else if (stressRequest['run']['deathbenefitonly'] === true) {
                    upFormData.incomeOptions = incomeOptions.deathBenefitsOnly.value
                    contributionType = contributionOptions.deathBenefits.value
                }else{
                    upFormData.incomeOptions = incomeOptions.deathBenefitsOnly.value
                    contributionType = contributionOptions.contribution.value || ""
                }
            }

            setUPFormData({
                ...formValues,
                ...upFormData,
                tranche: clientPolicy && clientPolicy.tranche && clientPolicy.tranche.trancheNumber,
                contributionType: contributionType,
                contributionAmount: contributionAmount, // contribution dividing issue NIWD-758
                deathBenefitAmount: !!deathBenefit ? deathBenefit : "",
                healthRating: actualHealthRating ? actualHealthRating : "",
                livingBenefit: livingBenefit,
                contributionFrequency: extractCF(caseDetails),
                InvestmentType: (stressRequest && stressRequest.InvestmentType) || caseDetails.clientInvestmentType || (isAlZ ? (atlantic ? InvestmentValues.SP500ALZ : InvestmentValues.Conservative) : InvestmentValues.SP500),
                primaryCarrier: clientPolicy && clientPolicy.carrierId,
                issueAge: (clientPolicy && clientPolicy.issueAge) || issueAge
            });
        }
    }, [caseDetails, atlantic, issueAge]);

    const formProps: any = {
        children: ProposalForm,
        contributionTypeConditionalFields,
        initialTouched:{totalCombinedContribution:true },
        enableReinitialize: true,
        enrollmentId,
        carrierId: caseDetails ? caseDetails.carrierId : 3,
        handleRunIllustration: isIulCase ? runEstimatorForIul : handleRunIllustration,
        incomeConditionalFields,
        initialValues: upFormData,
        illustrationFiles,
        isReportDataLoading,
        jVals,
        onSubmit: handleUploadProposal,
        setUploadCustomOrRunIllustration,
        uploadCustomOrRunIllustration,
        UploadProposalFields,
        validate: validate,
        validationSchema: uploadCustomOrRunIllustration === ProposalEnum.UPLOAD_CUSTOM_ILLUSTRATION ? uploadFormSchema : runIllustrationFormSchema,
        innerRef:formRef,
        stressRequest,
        setIsChanged,
        setEditIssueAge,
    };

    const dialogProps: GenericDialogProps = {
        actions: (
                <>
                    <Button
                            type="button"
                            variant="contained"
                            color="primary"
                            onMouseDown={() => {
                                handleRemoveFocus();
                                setEditIssueAge(false);
                            }}
                            className="btnContainedRed floatRight mr20"
                    >
                        Cancel
                    </Button>
                    <Button
                            type="submit"
                            variant="contained"
                            onClick={() => {
                                setIsChanged(true);
                                setEditIssueAge(false);
                            }}
                            color="primary"
                            className="floatRight mr20"
                    >
                        Edit
                    </Button>
                </>
        ),
        "aria-labelledby": "pop-up",
        content: ("The displayed value was generated from an automated source. Editing will override the value for this field only. The source will remain unchanged. Continue?"),
        onClose: () => {
            handleRemoveFocus();
            setEditIssueAge(false);
        },
        open: isEditIssueAge,
        title: "Edit Issue Age",
    };
    return (
        <>


            {
                caseDetails &&
                <Socket
                    callingFrom={callingFrom}
                    handleButtonClick={socketButtonClick}
                    handleReset={noop}
                    handleResult={noop}
                    isReportRequestAutomated={isReportRequestAutomated}
                    page={"estimator"}
                    requestId={requestId}
                    setIsEstimationDataLoading={setIsEstimationDataLoading}
                    setIsReportDataLoading={socketIsReportDataLoading}
                    setIsReportRequestCompleted={setIsReportRequestCompleted}
                    setLifeDeathBenefitSeeking={noop}
                    setResponseData={noop}
                    userId={client && client.userId}
                    closeAfterFullProcess={true}
                />
            }
            {
                isEstimationDataLoading &&
                <Backdrop className={classes.backdrop} open={isEstimationDataLoading}>
                    <TestingProcessDialog
                        getEstimationProgress={estimationActions.getEstimationProgress}
                        handleClose={() => setIsEstimationDataLoading(false)}
                        isEstimationDataLoading={isEstimationDataLoading}
                        open={isEstimationDataLoading}
                    />
                </Backdrop>
            }

            <FormikWrapper {...formProps} />

            {
                (ProposalEnum.RUN_ILLUSTRATION === uploadCustomOrRunIllustration) &&
                <UploadedDocumentTable
                    enrollmentId={enrollmentId as string}
                    onfetchFiles={(reports: any) => setIllustrationFiles(reports)}
                    setHasReports={setHasReports}
                    reportsLoading={isReportDataLoading}
                    userId={client.userId}
                    clientPolicy={clientPolicy}
                />
            }

            <GenericDialog {...dialogProps} />

            {
                isDebug() &&
                <pre>{JSON.stringify({uploadCustomOrRunIllustration}, null, 2)}</pre>
            }

        </>
    );
};

export default Content;
